import { fmtDate as _fmtDate, fmtDateTime as _fmtDateTime, fmtRelative as _fmtRelative } from '@/lib/datetime'

// Currency formatting
export function formatCurrency(amount: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount)
}

// Compact currency (e.g., $1.2K, $34.5K)
export function formatCurrencyCompact(amount: number): string {
  if (amount >= 1_000_000) return `$${(amount / 1_000_000).toFixed(1)}M`
  if (amount >= 1_000) return `$${(amount / 1_000).toFixed(1)}K`
  return formatCurrency(amount)
}

// Date formatting — delegates to lib/datetime for timezone-safe rendering
export function formatDate(date: string | Date | null | undefined, fmt = 'MMM d, yyyy'): string {
  return _fmtDate(date, fmt)
}

export function formatDateTime(date: string | Date | null | undefined): string {
  return _fmtDateTime(date)
}

export function formatRelativeTime(date: string | Date | null | undefined): string {
  return _fmtRelative(date)
}

// Phone formatting
export function formatPhone(phone: string): string {
  const cleaned = phone.replace(/\D/g, '')
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
  }
  if (cleaned.length === 11 && cleaned[0] === '1') {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`
  }
  return phone
}

// Name formatting
export function formatFullName(firstName?: string, lastName?: string): string {
  return [firstName, lastName].filter(Boolean).join(' ') || 'Unknown'
}

/**
 * Strip leading bracketed labels like "[DEMO]", "[TEST] ", "(draft)" etc.
 * from a display name so derived artifacts (initials, section letters,
 * sort keys) don't end up with junk characters.
 *
 * "[DEMO] Alicia Summers" → "Alicia Summers"
 * "(draft) Bob"           → "Bob"
 * "Charlie"               → "Charlie"
 */
export function stripLabelPrefix(name: string): string {
  return name.replace(/^\s*[[(][^\])]+[\])]\s*/g, '').trim()
}

// Initials — ignores bracketed prefixes and non-alpha characters so names
// like "[DEMO] Alicia Summers" resolve to "AS" instead of "[A".
export function getInitials(name: string): string {
  const cleaned = stripLabelPrefix(name)
  const tokens = cleaned.split(/\s+/).filter(Boolean)
  const letters: string[] = []
  for (const tok of tokens) {
    const m = tok.match(/[A-Za-z]/)
    if (m) letters.push(m[0])
    if (letters.length === 2) break
  }
  return letters.join('').toUpperCase() || '?'
}

// Number formatting
export function formatNumber(n: number): string {
  return new Intl.NumberFormat('en-US').format(n)
}

// Percentage
export function formatPercent(value: number, decimals = 1): string {
  return `${value.toFixed(decimals)}%`
}

// Payment terms
export const PAYMENT_TERM_LABELS: Record<string, string> = {
  due_on_receipt:    'Due on Receipt',
  due_on_completion: 'Due on Completion',
  net_15:            'Net 15 Days',
  net_30:            'Net 30 Days',
  net_45:            'Net 45 Days',
}

export function formatPaymentTerms(terms: string): string {
  const key = terms.toLowerCase()
  return PAYMENT_TERM_LABELS[key] ?? key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

// Truncate text
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text
  return `${text.slice(0, maxLength - 3)}...`
}

// Capitalize first letter
export function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

// Slug to title
export function slugToTitle(slug: string): string {
  return slug
    .split('-')
    .map(capitalize)
    .join(' ')
}
